import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg } from "@/lib/api";

const FREQS = [["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["four_weekly", "Every 4 weeks"], ["monthly", "Monthly"], ["custom", "Custom dates"]];

const CustomDates = ({ dates, onChange }) => {
  const [pick, setPick] = useState("");
  const add = (d) => {
    setPick("");
    if (!dates.includes(d)) onChange([...dates, d].sort());
  };
  return (
    <div className="space-y-2">
      <Label>Pay period end dates</Label>
      <DatePicker value={pick} onChange={add} testId="pay-custom-date-picker" />
      <div className="flex flex-wrap gap-2">
        {dates.map((d) => (
          <span key={d} className="flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700" data-testid={`pay-custom-date-${d}`}>
            {format(parseISO(d), "d MMM yyyy")}
            <button type="button" onClick={() => onChange(dates.filter((x) => x !== d))} data-testid={`pay-custom-remove-${d}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <p className="text-xs text-slate-500">Each period runs from the day after the previous end date. Add dates for the year ahead.</p>
    </div>
  );
};

export const PayPeriodSettings = () => {
  const [f, setF] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    api.get("/biz/pay-settings").then((r) => setF(r.data));
    api.get("/biz/pay-period", { params: { on: format(new Date(), "yyyy-MM-dd") } }).then((r) => setPreview(r.data)).catch(() => {});
  }, []);
  if (!f) return null;

  const save = async (e) => {
    e.preventDefault();
    try {
      const { frequency, anchor_date, month_start_day, custom_end_dates, pay_day_note, payroll_reminder, staff_summary } = f;
      await api.put("/biz/pay-settings", { frequency, anchor_date, month_start_day: Number(month_start_day), custom_end_dates, pay_day_note, payroll_reminder, staff_summary });
      const { data } = await api.get("/biz/pay-period", { params: { on: format(new Date(), "yyyy-MM-dd") } });
      setPreview(data);
      toast.success("Pay period saved");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/biz/payroll-reminder/test");
      toast.success(data.demo ? "Demo business: email skipped (would go to you)" : `Payroll email for ${data.period_start} – ${data.period_end} sent to ${data.to}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const previewSummary = async () => {
    try {
      const { data } = await api.post("/biz/staff-summary/preview");
      toast.success(data.demo ? "Demo business: preview skipped" : data.sample ? `Sample summary (example data) sent to ${data.to}` : `Preview of ${data.staff_name}'s summary sent to ${data.to}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const cyclic = ["weekly", "fortnightly", "four_weekly"].includes(f.frequency);
  return (
    <form onSubmit={save} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="pay-settings-form">
      <div>
        <h2 className="font-display text-xl font-semibold">Pay periods</h2>
        <p className="text-sm text-slate-500">Sets the timesheet period and the payroll CSV export.</p>
      </div>
      <div className="space-y-1.5">
        <Label>How often do you pay staff?</Label>
        <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}>
          <SelectTrigger data-testid="pay-frequency-select"><SelectValue /></SelectTrigger>
          <SelectContent>{FREQS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`pay-frequency-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {cyclic && (
        <div className="space-y-1.5">
          <Label>First day of any pay period</Label>
          <DatePicker value={f.anchor_date} onChange={(d) => setF({ ...f, anchor_date: d })} testId="pay-anchor-picker" />
          <p className="text-xs text-slate-500">Pick the start date of a past or current period. We repeat it from there.</p>
        </div>
      )}
      {f.frequency === "monthly" && (
        <div className="space-y-1.5">
          <Label>Period starts on day of month (1–28)</Label>
          <Input type="number" min="1" max="28" value={f.month_start_day} onChange={(e) => setF({ ...f, month_start_day: e.target.value })} data-testid="pay-month-day-input" />
        </div>
      )}
      {f.frequency === "custom" && <CustomDates dates={f.custom_end_dates} onChange={(d) => setF({ ...f, custom_end_dates: d })} />}
      <div className="space-y-1.5">
        <Label>Pay day note (optional)</Label>
        <Input placeholder="e.g. Paid the Friday after period end" value={f.pay_day_note || ""} onChange={(e) => setF({ ...f, pay_day_note: e.target.value })} data-testid="pay-note-input" />
      </div>
      <div className="rounded-lg border border-slate-200 p-3">
        <label className="flex items-center justify-between gap-3 text-sm font-medium">
          Email managers the payroll CSV when a pay period closes
          <Switch checked={f.payroll_reminder !== false} onCheckedChange={(v) => setF({ ...f, payroll_reminder: v })} data-testid="pay-reminder-switch" />
        </label>
        <p className="mt-1 text-xs text-slate-500">Sent at 7am the morning after each period ends, with the CSV attached.</p>
        <button type="button" onClick={sendTest} className="mt-2 text-xs font-semibold text-indigo-600 hover:underline" data-testid="pay-reminder-test-btn">Send me last period's payroll email now</button>
      </div>
      <div className="rounded-lg border border-slate-200 p-3">
        <label className="flex items-center justify-between gap-3 text-sm font-medium">
          Email each staff member their hours summary
          <Switch checked={f.staff_summary !== false} onCheckedChange={(v) => setF({ ...f, staff_summary: v })} data-testid="pay-staff-summary-switch" />
        </label>
        <p className="mt-1 text-xs text-slate-500">Approved hours, sleepover allowances and estimated gross pay, sent when the period closes. Only staff with approved timesheets are emailed.</p>
        <button type="button" onClick={previewSummary} className="mt-2 text-xs font-semibold text-indigo-600 hover:underline" data-testid="pay-staff-summary-preview-btn">Send me a sample staff summary</button>
      </div>
      {preview && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800" data-testid="pay-period-preview">Current period: {format(parseISO(preview.start), "d MMM")} – {format(parseISO(preview.end), "d MMM yyyy")}</p>}
      <Button type="submit" className="w-full rounded-full" data-testid="pay-settings-save-btn">Save pay period</Button>
    </form>
  );
};
